import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { getGateGroupGates, addGatesToGroup, removeGateFromGroup } from '../../../services/gateGroupService';
import { parseTableParams } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function GateGroupGatesTab({ groupId }) {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, gateId: null, name: '' });
  const [deleting, setDeleting] = useState(false);
  const [allGates, setAllGates] = useState([]);

  useEffect(() => {
    setFetching(true);
    getGateGroupGates(groupId)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [groupId, queryParams, refreshTrigger]);

  const baseColumns = useMemo(() => [
    {
      name: 'name', label: t('gate.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'buildingName', label: t('gate.buildingName'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'direction', label: t('gate.direction'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value) => {
          const map = { entry: t('gate.entry'), exit: t('gate.exit'), bidirectional: t('gate.bidirectional') };
          return map[value] || value || '-';
        },
      },
    },
  ], [t]);

  const handleRemoveClick = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, gateId: row.id, name: row.name });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await removeGateFromGroup(groupId, deleteConfirm.gateId);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, gateId: null, name: '' });
    setDeleting(false);
  }, [groupId, deleteConfirm.gateId]);

  const actionOptions = useMemo(() => [
    { type: 'delete', label: t('gateGroup.removeGate'), onClick: handleRemoveClick },
  ], [t, handleRemoveClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleAddClick = useCallback(() => {
    api.get('/v1/gates?pageSize=500').then((res) => {
      setAllGates(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
    setDrawerOpen(true);
  }, []);

  const handleSave = async (formData) => {
    try {
      await addGatesToGroup(groupId, [formData.gateId]);
      setDrawerOpen(false);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  };

  const schema = useMemo(() => [
    {
      name: 'gateId', label: t('gateGroup.addGate'), type: 'select', grid: 12, required: true,
      options: allGates, idKey: 'id', labelKey: 'name',
    },
  ], [t, allGates]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('gateGroup.removeGate')}
        message={t('gateGroup.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, gateId: null, name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('gateGroup.gates')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          newButtonLabel={t('gateGroup.addGate')}
          onNewButtonClick={handleAddClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleAddClick,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('gateGroup.addGate')}
        schema={schema}
        onSave={handleSave}
      />
    </Container>
  );
}
